import {
  AccountFormInput,
  AccountTypeDisplayText,
  CustomFieldEntityEnum,
  IDunnings,
  ILegalEntityResponseSchema,
  ITaxConnectionBaseSchema,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import { objectToSelectOptions } from '@monetize/utils/core';
import { FunctionComponent as FC } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { MdOpenInNew } from 'react-icons/md';
import { useAtomValue } from 'jotai';
import { useGetListData } from '../../api/queryUtils';
import { CustomFieldDataForm } from '../../components/CustomFields/CustomFieldDataForm';
import {
  MCheckbox,
  MCurrencySelect,
  MDivider,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MInput,
  MLink,
  MLockedTextOrContent,
  MText,
  MTextarea,
} from '../../components/Monetize';
import { MCustomSelect } from '../../components/Monetize/MCustomSelect/MCustomSelect';
import { MLegalEntitySelect } from '../../components/Monetize/MLegalEntitySelect';
import { ACCOUNTS } from '../../constants';
import { CUSTOM_ID_PLACEHOLDER } from '../../constants/placeholders';
import { TAX_ENTITY_USE_CODES } from '../../constants/taxes';
import { appGlobalDataState } from '../../store/global.store';
import { getAddress } from '../../utils/address';
import { isTaxExemptionSupported } from '../../utils/taxes';
import { AddressForm } from '../../components/Contacts/AddressForm';

interface AccountFormProps {
  control: Control<AccountFormInput>;
  setValue: UseFormSetValue<AccountFormInput>;
  watch: UseFormWatch<AccountFormInput>;
  errors: FieldErrors<AccountFormInput>;
  isReadOnly?: boolean;
  legalEntities?: ILegalEntityResponseSchema[];
  allowEditAccountAddress?: boolean;
}

const AccountForm: FC<AccountFormProps> = ({
  control,
  errors,
  isReadOnly,
  legalEntities = [],
  watch,
  allowEditAccountAddress = false,
}: AccountFormProps) => {
  const isReadOnlyProps = {
    isDisabled: isReadOnly,
    disabled: isReadOnly,
    isReadOnly,
    variant: isReadOnly ? 'readonly' : 'outline',
  } as const;
  const { hasMultipleCurrency, hasMultipleLegalEntity } =
    useAtomValue(appGlobalDataState);

  const { data: taxConnections } = useGetListData<
    ITaxConnectionBaseSchema[],
    ITaxConnectionBaseSchema[]
  >('tax', undefined, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });

  const { data: dunnings } = useGetListData<IDunnings>(
    'dunnings',
    {
      config: DEFAULT_PAGER,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24,
      gcTime: 1000 * 60 * 60 * 24,
    },
  );

  const isTaxExemptionEnabled = isTaxExemptionSupported(taxConnections);

  const billingAddress = watch('billingAddress');
  const shippingAddress = watch('shippingAddress');
  const customId = watch('customId');
  const ccEmails = watch('ccEmails');
  const bccEmails = watch('bccEmails');

  const defaultDunningProcessId = watch('defaultDunningProcessId');
  const selectedDunningProcess = dunnings?.content.find(
    (entity) => entity.id === defaultDunningProcessId,
  );

  const defaultLegalEntityId = watch('defaultLegalEntityId');
  const selectedLegalEntity = legalEntities.find(
    (entity) => entity.id === defaultLegalEntityId,
  );

  const formattedBillingAddress = getAddress(billingAddress, {
    addressFormat: selectedLegalEntity?.addressFormat,
  }).fullAddress;
  const formattedShippingAddress = getAddress(shippingAddress, {
    addressFormat: selectedLegalEntity?.addressFormat,
  }).fullAddress;
  const canEditAccountAddress = allowEditAccountAddress && !isReadOnly;

  return (
    <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
      <MGridItem colSpan={[12, null, null, 6]}>
        <MFormField error={errors.accountName} label="Account Name" isRequired>
          <Controller
            name="accountName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <MInput
                data-testid="account-company-name"
                {...field}
                {...isReadOnlyProps}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 6, 4, 2]}>
        <MFormField error={errors.accountType} label="Type" isRequired>
          <Controller
            name="accountType"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                itemTitle="title"
                itemValue="value"
                items={objectToSelectOptions(AccountTypeDisplayText)}
                {...field}
                {...isReadOnlyProps}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 6, 4, 2]}>
        <MFormField error={errors.status} label="Status" isRequired>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                itemTitle="name"
                itemValue="value"
                items={ACCOUNTS.STATUS_OPTIONS}
                {...field}
                {...isReadOnlyProps}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, null, 4, 2]}>
        <MFormField error={errors.isTestAccount} label="" skipLabel>
          <Controller
            name="isTestAccount"
            control={control}
            defaultValue
            render={({ field: { value, ...rest } }) => (
              <MCheckbox
                isChecked={value as any}
                {...rest}
                color="tPurple.dark"
                isDisabled={isReadOnlyProps.isDisabled}
                isReadOnly={isReadOnlyProps.isReadOnly}
              >
                Test Account
              </MCheckbox>
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, null, 6]}>
        <MFormField label="Billing Address">
          {canEditAccountAddress ? (
            <AddressForm formKeyPrefix="billingAddress." />
          ) : (
            <MLockedTextOrContent
              isLocked={!!isReadOnly}
              text={formattedBillingAddress}
            >
              <MText display="inline">
                {formattedBillingAddress || 'Blank'}
              </MText>
            </MLockedTextOrContent>
          )}
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, null, 6]}>
        <MFormField label="Shipping Address">
          {canEditAccountAddress ? (
            <AddressForm formKeyPrefix="shippingAddress." />
          ) : (
            <MLockedTextOrContent
              isLocked={!!isReadOnly}
              text={formattedShippingAddress}
            >
              <MText display="inline">
                {formattedShippingAddress || 'Blank'}
              </MText>
            </MLockedTextOrContent>
          )}
        </MFormField>
      </MGridItem>

      {hasMultipleLegalEntity && (
        <MGridItem
          colSpan={hasMultipleCurrency ? [12, null, 6] : [12, null, 12]}
        >
          <MFormField error={errors.defaultLegalEntityId} label="Legal Entity">
            <Controller
              name="defaultLegalEntityId"
              control={control}
              render={({ field }) => (
                <MLegalEntitySelect {...field} {...isReadOnlyProps} />
              )}
            />
          </MFormField>
        </MGridItem>
      )}
      {hasMultipleCurrency && (
        <MGridItem
          colSpan={hasMultipleLegalEntity ? [12, null, 6] : [12, null, 12]}
        >
          <MFormField error={errors.defaultCurrency} label="Default Currency">
            <Controller
              name="defaultCurrency"
              control={control}
              render={({ field }) => (
                <MCurrencySelect {...field} {...isReadOnlyProps} />
              )}
            />
          </MFormField>
        </MGridItem>
      )}

      <MGridItem colSpan={[12, null, 6]}>
        <MFormField
          error={errors.defaultDunningProcessId}
          label="Dunning for New Bill Groups"
        >
          <MLockedTextOrContent
            isLocked={!!isReadOnly}
            text={selectedDunningProcess?.name}
          >
            <Controller
              name="defaultDunningProcessId"
              control={control}
              render={({ field }) => (
                <MCustomSelect
                  placeholder="Select"
                  items={(dunnings?.content || []).map((x: IDunnings) => ({
                    title: x.name,
                    value: x.id,
                  }))}
                  clearable
                  {...field}
                  {...isReadOnlyProps}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>
      <MGridItem colSpan={[12, null, 6]}>
        <MFormField error={errors.customId} label="Custom ID">
          <MLockedTextOrContent isLocked={!!isReadOnly} text={customId}>
            <Controller
              name="customId"
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput
                  placeholder={CUSTOM_ID_PLACEHOLDER}
                  value={value || ''}
                  {...rest}
                  {...isReadOnlyProps}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={12}>
        <MFormField
          error={errors.ccEmails}
          label="Account-level CCs"
          data-testid="email-text-area"
          tooltipUsePopover={true}
          tooltip={
            <MText fontSize="xs" color="tWhite.base">
              Use to CC in Dunning process. Recommended to manage CC list in CRM
              and map to MonetizeNow.{' '}
              <MLink
                href="https://docs.monetizenow.io/docs/dunning#keep-everyone-informed-throughout-dunning-process"
                target="_blank"
                color="tWhite.base"
              >
                Learn more. <MIcon as={MdOpenInNew} />
              </MLink>
            </MText>
          }
        >
          <MLockedTextOrContent isLocked={!!isReadOnly} text={ccEmails}>
            <Controller
              name="ccEmails"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MTextarea
                  value={value || ''}
                  placeholder="Enter emails"
                  spellCheck={false}
                  rows={1}
                  {...rest}
                  {...isReadOnlyProps}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={12}>
        <MFormField
          error={errors.bccEmails}
          label="Account-level BCCs"
          data-testid="bcc-email-text-area"
          tooltipUsePopover={true}
          tooltip={
            <MText fontSize="xs" color="tWhite.base">
              Use this to BCC emails during the dunning process. Recommended to
              manage the BCC list in CRM and map it to MonetizeNow.{' '}
              <MLink
                href="https://docs.monetizenow.io/docs/dunning#keep-everyone-informed-throughout-dunning-process"
                target="_blank"
                rel="noopener noreferrer"
                color="tWhite.base"
              >
                Learn more. <MIcon as={MdOpenInNew} />
              </MLink>
            </MText>
          }
        >
          <MLockedTextOrContent isLocked={!!isReadOnly} text={bccEmails}>
            <Controller
              name="bccEmails"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MTextarea
                  value={value || ''}
                  placeholder="Enter emails"
                  spellCheck={false}
                  rows={1}
                  {...rest}
                  {...isReadOnlyProps}
                />
              )}
            />
          </MLockedTextOrContent>
        </MFormField>
      </MGridItem>

      {isTaxExemptionEnabled && (
        <>
          <MGridItem colSpan={12} mt={4}>
            <MDivider />
          </MGridItem>
          <MGridItem colSpan={6}>
            <MFormField
              error={errors.taxExemptionNumber}
              label="Tax Exemption Number"
            >
              <Controller
                name="taxExemptionNumber"
                control={control}
                render={({ field: { value, ...rest } }) => (
                  <MInput value={value || ''} {...rest} {...isReadOnlyProps} />
                )}
              />
            </MFormField>
          </MGridItem>
          <MGridItem colSpan={6}>
            <MFormField
              error={errors.taxEntityUseCode}
              label="Tax Exemption Reason"
            >
              <Controller
                name="taxEntityUseCode"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    clearable
                    items={TAX_ENTITY_USE_CODES}
                    {...field}
                    {...isReadOnlyProps}
                  />
                )}
              />
            </MFormField>
          </MGridItem>
        </>
      )}
      <MGridItem colSpan={12}>
        <Controller
          name="customFields"
          control={control}
          defaultValue={{}}
          render={({ field: { value, onChange } }) => (
            <CustomFieldDataForm
              entity={CustomFieldEntityEnum.ACCOUNT}
              value={value}
              setValue={(val) => onChange(val)}
              fieldColSpan={6}
              mode="modal"
              {...isReadOnlyProps}
            />
          )}
        />
      </MGridItem>
    </MGrid>
  );
};

export default AccountForm;
